import { BUN_PUBLIC_IMAGE_BASE_URL } from "@/env.json";

/**
 * Using a database to store this data is overkill, so we just keep it in a TS file for typesafety and ease of use.
 */
const SmartBloobin = "/projects/smart-bloobin.png";
const RedditsNightmare = "/projects/reddits-nightmare.png";

// RoastMe
const RoastMeHome = "/projects/roast-me/roastme.png";
const RoastMeThinking = "/projects/roast-me/thinking.png";
const RoastMeRoast = "/projects/roast-me/roast.png";
const RoastMeImages = [RoastMeHome, RoastMeThinking, RoastMeRoast];

// Stock Monitor
const StockMonitorDiscord = "/projects/stock-monitor/discord.png";
const StockMonitorAmazon = "/projects/stock-monitor/order.png";
const StockMonitorStatus = "/projects/stock-monitor/status.png";
const StockMonitorImages = [StockMonitorDiscord, StockMonitorStatus, StockMonitorAmazon];

// Blahaj Tours
const BlahajToursHome = "/projects/blahaj-tours/home.png";
const BlahajToursHome2 = "/projects/blahaj-tours/home-2.png";
const BlahajToursCategories = "/projects/blahaj-tours/categories.png";
const BlahajToursSearch = "/projects/blahaj-tours/search-sort.png";
const BlahajToursTourPage = "/projects/blahaj-tours/tour-edit.png";
const BlahajToursImages = [BlahajToursHome, BlahajToursHome2, BlahajToursCategories, BlahajToursSearch, BlahajToursTourPage];

// KitchenHaven
const KitchenHavenPatnry1 = "/projects/kitchen-haven/pantry-1.png";
const KitchenHavenPantry2 = "/projects/kitchen-haven/pantry-2.png";
const KitchenHavenRecipe1 = "/projects/kitchen-haven/recipe-1.png";
const KitchenHavenRecipe2 = "/projects/kitchen-haven/recipe-2.png";
const KitchenHavenSearch2 = "/projects/kitchen-haven/search-2.png";
const KitchenHavenImages = [KitchenHavenRecipe1, KitchenHavenRecipe2, KitchenHavenPatnry1, KitchenHavenPantry2, KitchenHavenSearch2];

const vercelBlobBaseUrl = BUN_PUBLIC_IMAGE_BASE_URL;

// Format the url of each image
const prepareImages = (images: string[], projectName: string, limitHeight?: boolean) => {
  return images.map((src, i) => ({ src: `${vercelBlobBaseUrl + src}`, alt: `${projectName} Project Screenshot ${i + 1}`, limitHeight }));
};

const projects = [
  {
    title: "Smart Bloobin",
    images: prepareImages([SmartBloobin], "Smart Bloobin"),
    description:
      "**AI-powered smart bin** that uses **RAG** to identify objects shown to it. It deduces the **material** the object is made of (down to PET recyclability), and only accepts **recyclable materials**.",
    repoLink: "https://github.com/Axiver/bloobin-detection-py",
    learnMoreLink: undefined,
    hackathon: true,
    techStack: ["Typescript", "Python", "OpenAI", "WebRTC", "Multi-Threading", "React", "TailwindCSS", "Raspberry Pi"],
  },
  {
    title: "RoastMe",
    images: prepareImages(RoastMeImages, "RoastMe"),
    description:
      "**AI-powered roast generator** that puts stand-up comedians to shame. RoastMe takes in your name and **consults the cosmos on all the decisions you've ever made in your life**, before generating a roast that'll **haunt you forever**.",
    repoLink: "https://github.com/kKar1503/roast-me",
    learnMoreLink: undefined,
    hackathon: true,
    techStack: ["Typescript", "Python", "Flask", "Ollama", "React", "TailwindCSS"],
  },
  {
    title: "Stock Monitor",
    images: prepareImages(StockMonitorImages, "Stock Monitor"),
    description:
      "**Highly-scalable multi-node bot network** using a **master-slave model**, regularly hitting product availability within **< 2s per drop**, and sends **instant notifications**. Reliably circumvents **Amazon's bot detection mechanisms**. Dedicated **automated purchaser nodes** on standby immediately attempt to buy in-stock items, maximising chances of success.",
    repoLink: undefined,
    learnMoreLink: undefined,
    hackathon: false,
    techStack: ["Node.js", "Puppeteer", "Socket.io", "Discord.js", "Javascript", "DigitalOcean API"],
  },
  {
    title: "Blahaj Tours",
    images: prepareImages(BlahajToursImages, "Blahaj Tours"),
    description:
      "**Full-stack tour agency platform** built with **J2EE** and **MySQL** that supports **end-to-end tour booking, payments, and administration**. Featuring third-party API integrations for **weather**, **location data**, and **payments**. Includes a **comprehensive admin dashboard** for managing users, tours, promotions, and sales analytics.",
    repoLink: "https://github.com/Axiver/JAD-CA1",
    learnMoreLink: undefined,
    hackathon: false,
    techStack: ["Java", "J2EE", "JSP", "Servlets", "MySQL", "OpenWeatherAPI", "Stripe", "Google Places API"],
  },
  {
    title: "KitchenHaven",
    images: prepareImages(KitchenHavenImages, "KitchenHaven", true),
    description:
      'An app to help users **manage their pantry**, **reduce food waste**, and **save money** by suggesting recipes based on the ingredients they **currently have**. It features a shopping list, pantry management, and recipe search with filters like "only dishes I can make". Users can personalise their account, get **reminders** about groceries when they are **near a grocery store**, and keep their data synced across devices.',
    repoLink: undefined,
    learnMoreLink: undefined,
    hackathon: false,
    techStack: ["Java", "Android SDK", "Gradle", "NoSQL", "Google Firebase"],
  },
  {
    title: "Reddit's Nightmare",
    images: prepareImages([RedditsNightmare], "Reddit's Nightmare"),
    description:
      "**An automation bot** that scrapes popular Reddit posts and **auto-uploads them to Instagram**. Features **OCR-powered hashtag generation**, **image processing** to meet Instagram's resolution requirements, a **guided first-time setup experience**, and **detailed logging via Winston** for easier debugging and maintenance.",
    repoLink: "https://github.com/Axiver/reddits-nightmare",
    learnMoreLink: undefined,
    hackathon: false,
    techStack: ["JavaScript", "OCR", "Image Processing", "Winston"],
  },
  {
    title: "Tuxcoin Gamble",
    description:
      "**A PHP-based gambling platform** backed by **MySQL** that allows users to wager using a cryptocurrency called **Tuxcoin**, running on the **Allium algo**. Includes **provably fair lotto and slots game modes**, **secure user accounts with 2FA-protected password resets**, **anti-bot protections**, and **direct interaction with the Tuxcoin blockchain via tuxcoind**.",
    repoLink: undefined,
    learnMoreLink: undefined,
    hackathon: false,
    techStack: ["PHP", "MySQL", "Cryptocurrency", "JsonRPC", "tuxcoind", "bitcoind", "Google ReCaptcha V2", "2FA"],
  },
];

export { projects };
